import {
  LOCK_IDLE_MS,
  OperationError,
  applyOperation,
  lockedFieldOf,
  newId,
  type ClientToServerEvents,
  type LockHolder,
  type Operation,
  type PresenceEntry,
  type RejectReason,
  type ServerToClientEvents,
  type Settings,
  type ValueMap,
  type View,
} from "@valuemap/core";
import { io, type Socket } from "socket.io-client";

/**
 * The browser side of live editing (brief 8.3).
 *
 * Edits apply to the local map at once (optimistic), so typing never waits for the round trip to
 * the server. Text edits to the same field are coalesced and sent 300 ms after the last keystroke
 * as whole-field values. The server applies operations last write wins and acknowledges each one.
 * On reconnect the client joins again, takes a fresh snapshot, then resends every operation that
 * was not acknowledged (the server ignores any it already applied). Pending edits live in memory
 * only, so nothing is left on a shared workshop device.
 *
 * With other people in the room, the client also keeps who is here and where (presence), which long
 * text fields someone else is typing in (soft locks), and who is leading (follow me).
 */

export type Phase = "connecting" | "ready" | "reconnecting" | "closed" | "error";

/** A field someone holds, and when this browser stops honouring the lock if it hears nothing. */
export interface HeldLock {
  holder: LockHolder;
  expiresAt: number;
}

export interface ClientState {
  phase: Phase;
  /** Set when the connection ended for good, for example an expired session or revoked access. */
  failure: string | null;
  /** The map as the person sees it: the server's copy plus their own unacknowledged edits. */
  map: ValueMap | null;
  settings: Settings | null;
  /** Edits not yet acknowledged by the server, including text waiting for the debounce. */
  pending: number;
  /** Revision of the server's copy that this client holds. */
  revision: number;
  /** Revision last written to Salesforce. */
  savedRevision: number;
  /** ISO time of the last write to Salesforce, if known. */
  savedAt: string | null;
  /** True while Salesforce cannot be reached, so every client shows a warning. */
  unsaved: boolean;
  /** A short message for the person, shown once (a toast). `at` orders it against other messages. */
  notice: { id: number; text: string; at: number } | null;
  /** This connection's ID, to tell its own presence and locks from other people's. */
  me: string | null;
  /** Everyone connected to the map, this connection included. */
  participants: readonly PresenceEntry[];
  /** Locked fields by path, held by anyone (this connection included). */
  locks: Readonly<Record<string, HeldLock>>;
}

/** What a component sees before the client exists. */
export const INITIAL_CLIENT_STATE: ClientState = {
  phase: "connecting",
  failure: null,
  map: null,
  settings: null,
  pending: 0,
  revision: 0,
  savedRevision: 0,
  savedAt: null,
  unsaved: false,
  notice: null,
  me: null,
  participants: [],
  locks: {},
};

type Sock = Pick<
  Socket<ServerToClientEvents, ClientToServerEvents>,
  "on" | "emit" | "close" | "connected"
>;

export interface MapClientOptions {
  /** Creates the socket when `start` is called. Defaults to a same-origin WebSocket-only connection. */
  connect?: () => Sock;
  debounceMs?: number;
  newOpId?: () => string;
  /** A held lock is released after this long without an edit (brief 8.3). */
  lockIdleMs?: number;
}

interface Queued {
  opId: string;
  op: Operation;
  sent: boolean;
  attempts: number;
}

const REJECT_TEXT: Record<RejectReason, string> = {
  not_found: "That part of the value map has changed, so your edit could not be applied.",
  invalid: "That change was not accepted.",
  limit: "The value map has reached a size limit for that.",
  duplicate: "That change was already made.",
  read_only: "This value map is read-only, so that change was not saved.",
  too_large:
    "That change would make the value map too large to save. Shorten some of the longer text.",
  unavailable: "That change could not be saved. Check your connection.",
  locked: "Someone else is typing in that field, so your change was not saved.",
};

const MAX_RESENDS = 5;

function defaultConnect(): Sock {
  return io({ transports: ["websocket"], path: "/socket.io" });
}

/** Two setField operations aimed at the same field. */
function sameField(a: Operation, b: Operation): boolean {
  return (
    a.type === "setField" &&
    b.type === "setField" &&
    a.goalId === b.goalId &&
    a.blockId === b.blockId &&
    a.path === b.path
  );
}

export class MapClient {
  private socket: Sock | undefined;
  private readonly connectSocket: () => Sock;
  private readonly listeners = new Set<() => void>();
  private readonly debounceMs: number;
  private readonly lockIdleMs: number;
  private readonly newOpId: () => string;
  private state: ClientState = INITIAL_CLIENT_STATE;
  private serverMap: ValueMap | null = null;
  private outbox: Queued[] = [];
  private debounce: ReturnType<typeof setTimeout> | undefined;
  private resendTimer: ReturnType<typeof setTimeout> | undefined;
  /** Expiry timers for every lock this browser knows about. */
  private readonly lockTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** Idle timers for locks this connection holds: release after a pause in typing. */
  private readonly idleTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private where: { view: View; goalId?: string } | null = null;
  private leading = false;
  private lastResync = 0;
  private noticeId = 0;
  private closed = false;

  constructor(
    private readonly mapId: string,
    options: MapClientOptions = {},
  ) {
    this.debounceMs = options.debounceMs ?? 300;
    this.lockIdleMs = options.lockIdleMs ?? LOCK_IDLE_MS;
    this.newOpId = options.newOpId ?? (() => newId("o"));
    this.connectSocket = options.connect ?? defaultConnect;
  }

  /**
   * Open the connection. Call it from an effect and call `close` in the cleanup; it can be called
   * again after `close` (React Strict Mode mounts, unmounts and mounts again), starting fresh.
   */
  start(): void {
    if (this.socket && !this.closed) return;
    this.closed = false;
    this.serverMap = null;
    this.outbox = [];
    this.state = INITIAL_CLIENT_STATE;
    this.socket = this.connectSocket();
    this.bind(this.socket);
    for (const l of this.listeners) l();
  }

  // ---------- external store ----------

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = (): ClientState => this.state;

  private set(patch: Partial<ClientState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  private notify(text: string): void {
    this.set({ notice: { id: ++this.noticeId, text, at: Date.now() } });
  }

  // ---------- locks ----------

  /** Who else is typing in a field, or null if it is free or this connection holds it. */
  lockedBy(fieldPath: string): LockHolder | null {
    const lock = this.state.locks[fieldPath];
    if (!lock || lock.holder.id === this.state.me || lock.expiresAt <= Date.now()) return null;
    return lock.holder;
  }

  /** On focus of a long text field: ask for its lock, unless someone else has it. */
  lockField(fieldPath: string): void {
    if (!this.state.settings?.canEdit || this.lockedBy(fieldPath)) return;
    if (this.socket?.connected) this.socket.emit("lock", { fieldPath });
    this.touchIdle(fieldPath);
  }

  /** On blur, or after a pause in typing: give the field back. */
  unlockField(fieldPath: string): void {
    // Claimed by this connection: asked for on focus, taken by typing, or granted by the server.
    // The server only releases a lock its holder sends, so a stray unlock does no harm.
    const claimed =
      this.idleTimers.has(fieldPath) || this.state.locks[fieldPath]?.holder.id === this.state.me;
    clearTimeout(this.idleTimers.get(fieldPath));
    this.idleTimers.delete(fieldPath);
    if (claimed && this.socket?.connected) this.socket.emit("unlock", { fieldPath });
  }

  private touchIdle(fieldPath: string): void {
    clearTimeout(this.idleTimers.get(fieldPath));
    const t = setTimeout(() => this.unlockField(fieldPath), this.lockIdleMs);
    this.idleTimers.set(fieldPath, t);
  }

  private setLock(fieldPath: string, holder: LockHolder | null, expiresInMs?: number): void {
    clearTimeout(this.lockTimers.get(fieldPath));
    this.lockTimers.delete(fieldPath);
    const others = Object.entries(this.state.locks).filter(([path]) => path !== fieldPath);
    if (!holder) {
      this.set({ locks: Object.fromEntries(others) });
      return;
    }
    const ttl = expiresInMs ?? this.lockIdleMs;
    this.set({
      locks: Object.fromEntries([...others, [fieldPath, { holder, expiresAt: Date.now() + ttl }]]),
    });
    // Forget the lock if nothing renews it, for example if the holder's dyno went away.
    this.lockTimers.set(
      fieldPath,
      setTimeout(() => this.setLock(fieldPath, null), ttl),
    );
  }

  private clearLocks(): void {
    for (const t of this.lockTimers.values()) clearTimeout(t);
    this.lockTimers.clear();
    for (const t of this.idleTimers.values()) clearTimeout(t);
    this.idleTimers.clear();
  }

  // ---------- presence and follow me ----------

  /** Tell the room where this person is. Sent only when it changes, and again after a reconnect. */
  setPresence(view: View, goalId?: string): void {
    if (this.where?.view === view && this.where.goalId === goalId) return;
    this.where = { view, goalId };
    if (this.state.phase === "ready") this.sendPresence();
  }

  private sendPresence(): void {
    if (!this.where || !this.socket?.connected) return;
    this.socket.emit("presence", this.where.goalId ? this.where : { view: this.where.view });
  }

  /** Facilitator only: ask everyone who has not opted out to follow this view and goal. */
  follow(enabled: boolean): void {
    this.leading = enabled;
    if (this.socket?.connected) this.socket.emit("follow", { enabled });
  }

  // ---------- editing ----------

  /**
   * Apply an edit now and send it. `coalesce` is for text typed into a field: the newest value
   * replaces any earlier unsent edit to the same field, and is sent after the debounce. Returns
   * false if the edit was not made (read-only, someone else is typing in the field, or the same
   * code the server runs refused it).
   */
  dispatch(op: Operation, options: { coalesce?: boolean } = {}): boolean {
    const { map, settings } = this.state;
    if (!map || !settings?.canEdit) return false;
    const field = lockedFieldOf(op);
    if (field) {
      if (this.lockedBy(field)) {
        this.notify(REJECT_TEXT.locked);
        return false;
      }
      this.touchIdle(field);
    }
    let next: ValueMap;
    try {
      next = applyOperation(map, op);
    } catch (err) {
      if (err instanceof OperationError) {
        this.notify(REJECT_TEXT[err.code]);
        return false;
      }
      throw err;
    }
    if (next === map) return true;

    const last = this.outbox.at(-1);
    if (options.coalesce && last && !last.sent && sameField(last.op, op)) {
      last.op = op;
    } else {
      this.outbox.push({ opId: this.newOpId(), op, sent: false, attempts: 0 });
    }
    this.set({ map: next, pending: this.outbox.length });

    if (options.coalesce) {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.send(), this.debounceMs);
    } else {
      this.send();
    }
    return true;
  }

  /** Send every unsent edit now, in the order they were made. */
  private send(): void {
    clearTimeout(this.debounce);
    this.debounce = undefined;
    if (!this.socket?.connected || this.state.phase !== "ready") return;
    for (const q of this.outbox) {
      if (q.sent) continue;
      q.sent = true;
      this.socket?.emit("op", { opId: q.opId, op: q.op, baseRevision: this.state.revision });
    }
  }

  /** The server's copy with this person's unacknowledged edits applied on top. */
  private rebuild(): void {
    if (!this.serverMap) return;
    let map = this.serverMap;
    for (const q of this.outbox) {
      try {
        map = applyOperation(map, q.op);
      } catch {
        // The target changed under this edit. The server will reject it and clear it.
      }
    }
    this.set({ map, pending: this.outbox.length });
  }

  private drop(opId: string): Queued | undefined {
    const i = this.outbox.findIndex((q) => q.opId === opId);
    if (i < 0) return undefined;
    return this.outbox.splice(i, 1)[0];
  }

  /** Ask for a fresh snapshot, at most once a second. */
  private resync(): void {
    const now = Date.now();
    if (now - this.lastResync < 1000) return;
    this.lastResync = now;
    this.join();
  }

  private join(): void {
    this.socket?.emit("join", { mapId: this.mapId, lastRevision: this.state.revision });
  }

  // ---------- socket events ----------

  private bind(s: Sock): void {
    // Connection lifecycle events come from Socket.IO itself.
    const lifecycle = s as unknown as {
      on(event: string, listener: (...args: unknown[]) => void): void;
    };

    lifecycle.on("connect", () => {
      this.join();
    });
    lifecycle.on("disconnect", () => {
      if (this.closed || this.state.phase === "error") return;
      // Unsent and unacknowledged edits stay in the outbox and go out after the next snapshot.
      for (const q of this.outbox) q.sent = false;
      // The server releases this connection's locks; others' locks arrive in the next snapshot.
      this.clearLocks();
      this.set({ phase: "reconnecting", locks: {}, participants: [] });
    });
    lifecycle.on("connect_error", (err: unknown) => {
      if (this.closed) return;
      if ((err as Error | undefined)?.message === "unauthenticated") {
        this.fail("Your session has ended. Sign in again to keep editing.");
        return;
      }
      this.set({ phase: this.state.map ? "reconnecting" : "connecting" });
    });

    s.on("snapshot", (snap) => {
      this.serverMap = snap.map;
      this.clearLocks();
      this.set({
        phase: "ready",
        settings: snap.settings,
        revision: snap.revision,
        savedRevision: snap.save.revision,
        savedAt: snap.save.at ?? this.state.savedAt,
        unsaved: snap.save.status === "unsaved",
        me: snap.me,
        participants: snap.participants,
        locks: {},
      });
      for (const l of snap.locks) {
        if (l.holder) this.setLock(l.fieldPath, l.holder, l.expiresInMs ?? this.lockIdleMs);
      }
      if (!snap.settings.canEdit && this.outbox.length > 0) {
        this.outbox = [];
        this.notify(REJECT_TEXT.read_only);
      }
      for (const q of this.outbox) q.sent = false;
      this.rebuild();
      this.send();
      // A new connection starts with no presence or leadership on the server: restore them.
      this.sendPresence();
      if (this.leading && snap.settings.canLead) this.socket?.emit("follow", { enabled: true });
    });

    s.on("applied", ({ op, revision }) => {
      if (!this.serverMap || revision <= this.state.revision) return;
      if (revision !== this.state.revision + 1) return this.resync();
      try {
        this.serverMap = applyOperation(this.serverMap, op);
      } catch {
        return this.resync();
      }
      this.set({ revision });
      this.rebuild();
    });

    s.on("ack", ({ opId, revision }) => {
      const q = this.drop(opId);
      if (!q || !this.serverMap) return;
      if (revision === this.state.revision + 1) {
        try {
          this.serverMap = applyOperation(this.serverMap, q.op);
          this.set({ revision });
        } catch {
          this.resync();
        }
      } else if (revision > this.state.revision) {
        // Other people's changes that came before ours have not reached us yet.
        this.resync();
      }
      this.rebuild();
    });

    s.on("reject", ({ opId, reason }) => {
      const q = opId ? this.outbox.find((x) => x.opId === opId) : undefined;
      if (q && reason === "unavailable" && q.attempts < MAX_RESENDS) {
        // The room was busy: keep the edit and try again shortly.
        q.attempts += 1;
        q.sent = false;
        clearTimeout(this.resendTimer);
        this.resendTimer = setTimeout(() => this.send(), 1000 * q.attempts);
        return;
      }
      if (opId) this.drop(opId);
      this.notify(REJECT_TEXT[reason]);
      this.rebuild();
    });

    s.on("saved", ({ revision, at }) => {
      this.set({
        savedRevision: Math.max(this.state.savedRevision, revision),
        savedAt: at,
        unsaved: false,
      });
    });

    s.on("unsaved", () => this.set({ unsaved: true }));

    s.on("presence", ({ participants }) => this.set({ participants }));

    s.on("lock", ({ fieldPath, holder, expiresInMs }) => {
      this.setLock(fieldPath, holder, expiresInMs);
      if (holder && holder.id === this.state.me) this.touchIdle(fieldPath);
    });

    s.on("error", ({ code, message }) => {
      if (code === "bad_request") return this.notify(message);
      this.fail(message);
    });
  }

  private fail(message: string): void {
    this.clearLocks();
    this.set({ phase: "error", failure: message, locks: {}, participants: [] });
    this.socket?.close();
  }

  close(): void {
    this.closed = true;
    clearTimeout(this.debounce);
    clearTimeout(this.resendTimer);
    this.clearLocks();
    this.socket?.close();
    this.set({ phase: "closed" });
  }
}
